import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Provider de confirmação imperativa: substitui window.confirm por um Dialog Glass. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (result: boolean) => {
    setOpen(false);
    resolver.current?.(result);
    resolver.current = null;
  };

  const isDestructive = opts?.variant === 'destructive';
  const Icon = isDestructive ? Trash2 : AlertTriangle;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : settle(false))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3.5">
              <span
                className={cn(
                  'grid size-10 shrink-0 place-items-center rounded-xl',
                  isDestructive ? 'bg-destructive/12 text-destructive' : 'bg-primary/12 text-primary',
                )}
              >
                <Icon className="size-5" />
              </span>
              <div className="min-w-0">
                <DialogTitle>{opts?.title}</DialogTitle>
                {opts?.description ? (
                  <DialogDescription className="mt-1.5">{opts.description}</DialogDescription>
                ) : null}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => settle(false)}>
              {opts?.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button variant={isDestructive ? 'destructive' : 'primary'} onClick={() => settle(true)}>
              {opts?.confirmLabel ?? 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm deve ser usado dentro de <ConfirmProvider>');
  return ctx;
}
